/**
 * ToolDetailPage — Individual tool detail with interactive components
 *
 * Shows tool description, character attribution, interactive tool component,
 * and collapsible step-by-step instructions for reference.
 */

import React, { lazy, Suspense, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

import { useProgress } from '../context/ProgressContext';
import { useGamification } from '../context/GamificationContext';
import { getCharacterById } from '../data/characters';
import { TOOLS } from '../data/toolkit';
import CharacterDialogue from '../components/characters/CharacterDialogue';
import Button from '../components/shared/Button';
import Card from '../components/shared/Card';
import { recordToolUsed } from '../services/analytics';

// Lazy-load interactive tool components
const toolComponentMap = {
  FearCompanion: lazy(() => import('../components/toolkit/tools/FearCompanion')),
  GroundFinder: lazy(() => import('../components/toolkit/tools/GroundFinder')),
  MessageDistillery: lazy(() => import('../components/toolkit/tools/MessageDistillery')),
  AudiencePortrait: lazy(() => import('../components/toolkit/tools/AudiencePortrait')),
  FeelingCompass: lazy(() => import('../components/toolkit/tools/FeelingCompass')),
  PresencePrep: lazy(() => import('../components/toolkit/tools/PresencePrep')),
  MessageArchitect: lazy(() => import('../components/toolkit/tools/MessageArchitect')),
  CourtyardJournal: lazy(() => import('../components/toolkit/tools/CourtyardJournal')),
  CompleteSpeakingPrep: lazy(() => import('../components/toolkit/tools/CompleteSpeakingPrep')),
  QuickReset: lazy(() => import('../components/toolkit/tools/QuickReset')),
};

const ToolDetailPage = () => {
  const { toolId } = useParams();
  const { toolkitUsage, markToolUsed, getAchievementContext } = useProgress();
  const { addXP, checkAchievements } = useGamification();
  const [showSteps, setShowSteps] = useState(false);

  const tool = TOOLS.find((t) => t.id === toolId);

  if (!tool) {
    return <Navigate to="/toolkit" replace />;
  }

  const character = getCharacterById(tool.character);
  // Tool-specific in-voice introduction; the speaker can differ from the
  // owning character (e.g. shared 'all' tools are introduced by one guide)
  const introCharacter = tool.intro ? getCharacterById(tool.intro.characterId) : character;
  const introText = tool.intro?.text || 'This is one of my favourite tools. Let me walk you through it.';
  const steps = tool.steps || [];
  const hasUsed = toolkitUsage.includes(tool.id);
  const InteractiveComponent = tool.component ? toolComponentMap[tool.component] : null;

  const handleMarkUsed = () => {
    if (!hasUsed) {
      markToolUsed(tool.id);
      addXP(25, 'Tool used');
      recordToolUsed(tool.id); // the tool's id only, never what is written in it
      // State updates are async, so include the tool just marked by hand
      checkAchievements(getAchievementContext({ toolsUsed: toolkitUsage.length + 1 }));
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${tool.name} | Toolkit | Finding Your Voice`}</title>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">
        {/* Back */}
        <Link
          to="/toolkit"
          className="inline-flex items-center gap-1 text-sm text-courtyard-stone/80 hover:text-courtyard-stone"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> All Tools
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`rounded-xl p-6 border-l-4 ${
            tool.character === 'philemon' ? 'bg-philemon-bg/30 border-l-courtyard-olive' :
            tool.character === 'xenia' ? 'bg-xenia-bg/30 border-l-courtyard-aegean' :
            tool.character === 'all' ? 'bg-courtyard-gold/10 border-l-courtyard-gold' :
            'bg-theron-bg/30 border-l-courtyard-terracotta'
          }`}
        >
          <h1 className="font-display text-3xl font-bold text-courtyard-stone">
            {tool.name}
          </h1>
          <p className="text-courtyard-stone/80 mt-2 leading-relaxed">
            {tool.description}
          </p>

          {/* The evenings this tool belongs to */}
          {tool.relatedModules?.length > 0 && (
            <p className="text-xs text-courtyard-stone/80 mt-2">
              Goes with {tool.relatedModules.length === 1 ? 'Evening' : 'Evenings'} {tool.relatedModules.join(', ')}
            </p>
          )}

          {/* Character Attribution */}
          {introCharacter && (
            <div className="mt-4">
              <CharacterDialogue
                characterId={introCharacter.id}
                speaker={introCharacter.name}
                text={introText}
              />
            </div>
          )}
        </motion.div>

        {/* Interactive Component */}
        {InteractiveComponent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-white/40 border border-courtyard-gold/15 rounded-xl p-5"
          >
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-12 text-courtyard-stone/80 text-sm">
                  Loading tool…
                </div>
              }
            >
              <InteractiveComponent />
            </Suspense>
          </motion.div>
        )}

        {/* Collapsible Step-by-Step Instructions */}
        {steps.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <button
              onClick={() => setShowSteps(!showSteps)}
              className="flex items-center gap-2 text-sm text-courtyard-stone/80 hover:text-courtyard-stone"
            >
              {showSteps ? <ChevronUp className="w-4 h-4" aria-hidden="true" /> : <ChevronDown className="w-4 h-4" aria-hidden="true" />}
              {showSteps ? 'Hide' : 'View'} step-by-step instructions
            </button>

            {showSteps && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-4 space-y-4"
              >
                <h2 className="font-display text-xl font-bold text-courtyard-stone">
                  How to Use This Tool
                </h2>
                <ol className="space-y-4">
                  {steps.map((step, i) => (
                    <li
                      key={i}
                      className="bg-white/60 border border-courtyard-gold/15 rounded-xl p-4 flex gap-4"
                    >
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-courtyard-terracotta/20 to-courtyard-gold/20 text-courtyard-clay text-sm font-bold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        {step.title && (
                          <p className="font-display font-bold text-courtyard-stone text-sm mb-1">
                            {step.title}
                          </p>
                        )}
                        <p className="text-[15px] text-courtyard-stone/75 leading-relaxed">
                          {step.instruction || step}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Usage Tracking */}
        <motion.div
          className="pt-6 border-t border-courtyard-gold/20 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {hasUsed ? (
            <Card className="p-5 bg-courtyard-olive/5 border-courtyard-olive/20 inline-block">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-courtyard-olive-dark" aria-hidden="true" />
                <span className="font-bold text-courtyard-stone">
                  You've used this tool
                </span>
              </div>
            </Card>
          ) : (
            <Button variant="primary" onClick={handleMarkUsed}>
              I've Used This Tool
            </Button>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default ToolDetailPage;
